// MessagesPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Container,
    IconButton,
    Paper,
    Stack,
    Typography,
    Avatar,
    CircularProgress,
    Tooltip,
    Card,
    CardContent,
    CardActions,
    Button,
    Alert,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import PersonIcon from "@mui/icons-material/Person";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    getAllMessages,
    getReservationAnalysis,
    runReservationAnalysis,
} from "../api/AiAgentApi";

function formatTs(ts) {
    try {
        return new Date(ts).toLocaleString("ru-RU");
    } catch {
        return ts ?? "";
    }
}

function formatMessageText(text) {
    if (!text) return "";
    
    let processedText = text;
    
    // Обработка литеральных \n если они пришли как строка "\\n"
    if (typeof processedText === 'string') {
        processedText = processedText.replace(/\\n/g, '\n');
    }

    try {
        // Пробуем распарсить как JSON
        const parsed = JSON.parse(processedText);
        if (typeof parsed === 'object' && parsed !== null) {
            return JSON.stringify(parsed, null, 2);
        }
    } catch (e) {
        // Если не JSON, оставляем как есть
    }
    
    return processedText;
}

const ROLE_META = {
    USER: {
        side: "left",
        bg: (theme) => theme.palette.background.paper,
        fg: (theme) => theme.palette.text.primary,
        avatar: <PersonIcon />,
        colorKey: "default",
    },
    BOT: {
        side: "right",
        bg: (theme) => theme.palette.primary.main,
        fg: (theme) => theme.palette.primary.contrastText,
        avatar: <SmartToyIcon />,
        colorKey: "primary",
    },
    SYSTEM: {
        side: "right",
        bg: (theme) => theme.palette.grey[800],
        fg: (theme) => theme.palette.common.white,
        avatar: <SettingsSuggestIcon />,
        colorKey: "system",
    },
};

function MessageBubble({ msg }) {
    const meta = ROLE_META[msg.role] ?? ROLE_META.USER;
    const isRight = meta.side === "right";

    return (
        <Stack
            direction={isRight ? "row-reverse" : "row"}
            spacing={1.5}
            alignItems="flex-end"
            sx={{ width: "100%" }}
        >
            <Avatar
                sx={(theme) => ({
                    bgcolor:
                        msg.role === "BOT"
                            ? theme.palette.primary.main
                            : msg.role === "SYSTEM"
                                ? theme.palette.grey[700]
                                : theme.palette.grey[500],
                    color: theme.palette.common.white,
                    width: 32,
                    height: 32,
                })}
            >
                {ROLE_META[msg.role]?.avatar ?? <PersonIcon />}
            </Avatar>

            <Box sx={{ maxWidth: { xs: "78%", sm: "70%", md: "60%" } }}>
                <Paper
                    elevation={1}
                    sx={(theme) => ({
                        px: 1.5,
                        py: 1,
                        borderRadius: 3,
                        bgcolor: meta.bg(theme),
                        color: meta.fg(theme),
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: msg.text?.trim()?.startsWith("{") || msg.text?.trim()?.startsWith("[") ? "monospace" : "inherit",
                        fontSize: msg.text?.trim()?.startsWith("{") || msg.text?.trim()?.startsWith("[") ? "0.85rem" : "inherit",
                    })}
                >
                    {formatMessageText(msg.text)}
                </Paper>
                <Typography
                    variant="caption"
                    sx={{
                        mt: 0.5,
                        display: "inline-block",
                        opacity: 0.7,
                        ...(isRight ? { float: "right" } : {}),
                    }}
                >
                    {formatTs(msg.createAt)}
                </Typography>
            </Box>
        </Stack>
    );
}

export default function MessagesPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const reservationId = searchParams.get("reservationId");
    const [msgs, setMsgs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // анализ
    const [analysis, setAnalysis] = useState("");
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisErr, setAnalysisErr] = useState("");

    // контейнер и якорь для автоскролла
    const scrollContainerRef = useRef(null);
    const bottomRef = useRef(null);

    // Загрузка сообщений
    useEffect(() => {
        if (!reservationId) return;
        const load = async () => {
            setLoading(true);
            setErr("");
            try {
                const data = await getAllMessages(reservationId);
                setMsgs(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error(e);
                setErr("Не удалось загрузить сообщения");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [reservationId]);

    // Автопрокрутка
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [msgs]);

    // Загрузка текущего анализа
    const fetchAnalysis = async () => {
        if (!reservationId) return;
        setAnalysisLoading(true);
        setAnalysisErr("");
        try {
            const res = await getReservationAnalysis(reservationId);
            setAnalysis(res?.messageAnalysis ?? "");
        } catch (e) {
            console.error(e);
            setAnalysisErr("Не удалось загрузить анализ");
        } finally {
            setAnalysisLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalysis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationId]);

    // Триггер анализа
    const handleRunAnalysis = async () => {
        if (!reservationId) return;
        setAnalysisLoading(true);
        setAnalysisErr("");
        try {
            const res = await runReservationAnalysis(reservationId);
            setAnalysis(res ?? "");
        } catch (e) {
            console.error(e);
            setAnalysisErr("Не удалось запустить анализ");
        } finally {
            setAnalysisLoading(false);
        }
    };

    const headerTitle = useMemo(
        () => `Сообщения (бронь #${reservationId ?? "—"})`,
        [reservationId]
    );

    return (
        <Container maxWidth="md" sx={{ py: 2 }}>
            {/* Заголовок */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={() => navigate(-1)} size="small">
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6">{headerTitle}</Typography>
                </Stack>
            </Stack>

            {/* Карточка анализа */}
            <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Краткая выжимка по диалогу
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {analysisLoading && <CircularProgress size={18} />}
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleRunAnalysis}
                                disabled={analysisLoading || !reservationId}
                            >
                                Отправить на аналитику
                            </Button>
                        </Stack>
                    </Stack>

                    {analysisErr && (
                        <Alert severity="error" sx={{ mt: 1.5 }}>
                            {analysisErr}
                        </Alert>
                    )}

                    <Typography
                        variant="body2"
                        sx={{
                            mt: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            opacity: analysis ? 1 : 0.7,
                            fontFamily: analysis?.trim()?.startsWith("{") || analysis?.trim()?.startsWith("[") ? "monospace" : "inherit",
                        }}
                    >
                        {analysis ? formatMessageText(analysis) : "Пока нет данных анализа."}
                    </Typography>
                </CardContent>
            </Card>

            {/* Область сообщений */}
            <Paper
                ref={scrollContainerRef}
                variant="outlined"
                sx={{
                    height: { xs: "70vh", md: "72vh" },
                    p: 2,
                    overflowY: "auto",
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    backgroundImage: (theme) =>
                        theme.palette.mode === "dark"
                            ? "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"
                            : "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.01))",
                }}
            >
                {loading && (
                    <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, py: 4 }} spacing={1}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Загрузка…
                        </Typography>
                    </Stack>
                )}

                {!loading && err && (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="error">
                            {err}
                        </Typography>
                    </Stack>
                )}

                {!loading && !err && (!msgs || msgs.length === 0) && (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Сообщений нет
                        </Typography>
                    </Stack>
                )}

                {!loading && !err && msgs?.map((m) => (
                    <MessageBubble key={m.id ?? Math.random()} msg={m} />
                ))}

                <div ref={bottomRef} />
            </Paper>

            {/* Легенда ролей */}
            <Stack direction="row" spacing={2} sx={{ mt: 1 }} alignItems="center">
                <Tooltip title="Ответы ассистента">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <SmartToyIcon fontSize="small" />
                        <Typography variant="caption">BOT</Typography>
                    </Stack>
                </Tooltip>
                <Tooltip title="Системные сообщения/промпты">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <SettingsSuggestIcon fontSize="small" />
                        <Typography variant="caption">SYSTEM</Typography>
                    </Stack>
                </Tooltip>
                <Tooltip title="Сообщения пользователя">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <PersonIcon fontSize="small" />
                        <Typography variant="caption">USER</Typography>
                    </Stack>
                </Tooltip>
            </Stack>
        </Container>
    );
}
